import {
  CardContent,
  Box,
  Typography,
  Divider,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import logoFull from '../../../../../assets/images/logo-full.png';
import { purpleText, normalText } from '../../styles';
import { CodeSendRenewalSchema } from '../../validations';
import { radioCheckedIcon, radioIcon } from './CustomRadio';
import {
  sxCardContent,
  sxLogoBox,
  sxLogoTitle,
  sxDivider,
  sxMainBox,
  sxDetailsGrid,
  sxDetailsItem,
  sxSubtitle,
  sxForm,
  sxRadioGroup,
  sxRadio,
  sxErrorText,
  sxButton
} from './styles';
import CustomizedInputs from '../../../../../components/Input';
import { SendCode } from './send-code';
import Button from '~/components/Button';
import { Data } from '../../interfaces/contractinfo';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { sendCode } from '~/services/modules/users';
import { useState } from 'react';
import { storeState } from '~/store';

function formatPhone(phone: string): string {
  return phone.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
}

export type AuthRenewalForm = yup.InferType<typeof CodeSendRenewalSchema>;

interface Props {
  data: Data;
}

export function AuthRenewal({ data }: Props) {
  const formattedStartAt = format(parseISO(data.start_at), 'dd/MM/yyyy', { locale: ptBR });
  const formattedEndAt = format(parseISO(data.end_at), 'dd/MM/yyyy', { locale: ptBR });

  const formattedPhone = formatPhone(data.insured.mobile_phone);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<AuthRenewalForm>({
    resolver: yupResolver(CodeSendRenewalSchema),
    defaultValues: {
      method: 'sms',
      email: data.insured.email,
      phone: formattedPhone
    }
  });

  const method = watch('method');
  const [, setPayload] = useState<{ document: string; type: string }>();
  const [loading, setLoading] = useState(false);

  const onSendCode = async (formData: AuthRenewalForm) => {
    setLoading(true);
    const codeType = formData.method === 'sms' ? 1 : 2;
    setPayload({ document: data.insured.document, type: formData.method });
    try {
      await sendCode(data.insured.document, codeType);
      storeState.addToast({
        title: 'Sucesso',
        message: 'Código enviado com sucesso',
        type: 'success'
      });
    } catch (e) {
      console.error(e);
      storeState.addToast({
        title: 'Erro',
        message: 'Erro ao enviar código',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const dynamicDetails: { label: string; value: string | number }[] = [
    { label: 'Segurado:', value: data.insured.name },
    { label: 'Certificado nº', value: data.contract_code },
    { label: 'Vigência', value: `${formattedStartAt} até ${formattedEndAt}` },
    { label: 'Marca', value: data.insured_item.manufacturer || 'N/A' },
    { label: 'Modelo', value: data.insured_item.model || 'N/A' },
    { label: 'Chassi do equipamento', value: data.insured_item.serial_number || 'N/A' }
  ];

  return (
    <CardContent sx={sxCardContent}>
      <Box sx={sxLogoBox}>
        <img width={200} src={logoFull} alt="Logo" />
        <Typography variant="h6" align="center" sx={sxLogoTitle}>
          Renovação de Contratação
        </Typography>
      </Box>
      <Divider variant="middle" color="#323232" sx={sxDivider} />
      <Box sx={sxMainBox}>
        <Grid container spacing={1.5} sx={sxDetailsGrid}>
          {dynamicDetails.map((item, idx) => (
            <Grid key={idx} item xs={12} sm={6} sx={sxDetailsItem}>
              <Typography variant="subtitle1" sx={purpleText}>
                {item.label}
              </Typography>
              <Typography variant="subtitle2" sx={normalText}>
                {item.value}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Typography variant="subtitle2" sx={sxSubtitle}>
          Escolha onde deseja receber seu código de autenticação
        </Typography>
        <form onSubmit={handleSubmit(onSendCode)} style={sxForm}>
          <Controller
            name="method"
            control={control}
            render={({ field }) => (
              <>
                <RadioGroup row value={field.value} sx={sxRadioGroup} onChange={field.onChange}>
                  <FormControlLabel
                    value="email"
                    control={<Radio icon={radioIcon} checkedIcon={radioCheckedIcon} sx={sxRadio} />}
                    label="E-mail"
                  />
                  <FormControlLabel
                    value="sms"
                    control={<Radio icon={radioIcon} checkedIcon={radioCheckedIcon} sx={sxRadio} />}
                    label="SMS"
                    sx={{ margin: 0, alignItems: 'center' }}
                  />
                </RadioGroup>
                {errors.method && (
                  <Typography variant="caption" color="error" sx={sxErrorText}>
                    {errors.method.message}
                  </Typography>
                )}
              </>
            )}
          />
          {method === 'email' && (
            <div>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <CustomizedInputs
                    {...field}
                    label="Email"
                    disabled
                    helperText={fieldState.error?.message}
                    error={!!fieldState.error}
                    isDirty={!!field.value}
                    style={{ width: '13rem' }}
                  />
                )}
              />
            </div>
          )}
          {method === 'sms' && (
            <div>
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <CustomizedInputs
                    {...field}
                    style={{ width: '13rem' }}
                    label="Celular"
                    disabled
                    helperText={fieldState.error?.message}
                    error={!!fieldState.error}
                    isDirty={!!field.value}
                    mask="phone"
                  />
                )}
              />
            </div>
          )}
          <Button variant="contained" type="submit" sx={sxButton} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Enviar código'}
          </Button>
        </form>
      </Box>
      <Divider variant="middle" color="#323232" sx={sxDivider} />
      <SendCode data={data} type={method === 'sms' ? 1 : 2} />
    </CardContent>
  );
}
